"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";

type Template = {
  id: string;
  name: string;
  description: string | null;
  basePrice: string | null;
  targetWeight: number | null;
};

export function MysteryBoxPicker({ templates }: { templates: Template[] }) {
  const router = useRouter();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAddToCart() {
    if (!selectedId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/box/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          templateId: selectedId,
          selections: [],
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to create mystery box");
      }

      router.push("/cart");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 gap-4">
        {templates.map((t) => (
          <button
            key={t.id}
            onClick={() => setSelectedId(t.id)}
            className={`bg-white rounded-xl p-6 text-left border-2 transition-all ${
              selectedId === t.id
                ? "border-[var(--color-accent)] shadow-md"
                : "border-transparent hover:shadow-md"
            }`}
          >
            <h3 className="font-heading font-bold text-lg mb-2">{t.name}</h3>
            <p className="text-sm text-gray-600 mb-3">{t.description}</p>
            {t.basePrice ? (
              <div className="text-2xl font-heading font-bold text-[var(--color-accent)]">
                £{parseFloat(t.basePrice).toFixed(2)}
              </div>
            ) : (
              <div className="text-sm font-medium text-gray-400">Price TBC</div>
            )}
            {t.targetWeight && (
              <div className="text-sm text-gray-500 mt-1">
                Target: {(t.targetWeight / 1000).toFixed(1)}kg
              </div>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-sm text-[var(--color-destructive)] bg-red-50 border border-red-200 rounded-lg p-3">
          {error}
        </div>
      )}

      <button
        onClick={handleAddToCart}
        disabled={!selectedId || loading}
        className="w-full px-6 py-3 bg-[var(--color-accent)] text-white rounded-lg font-medium hover:bg-[var(--color-accent-light)] transition-colors disabled:opacity-50"
      >
        {loading ? "Adding..." : "Add Mystery Box to Cart"}
      </button>
    </div>
  );
}
